import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { onAuthStateChanged, onIdTokenChanged, type User } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { type Usuario } from "@ida/core";
import { BrowserRouter, Link, Navigate, NavLink, Route, Routes, useParams } from "react-router-dom";
import {
  auth, chamar, db, entrarModoDocumentacao, MODO_DOCUMENTACAO, sair,
} from "./firebase.js";
import {
  capturarAtribuicao, conversaoGoogle, evento, identificar, resetarTelemetria,
} from "./telemetria.js";
import { garantirAcessoProduto } from "./acesso-produto.js";
import { temPermissaoAdminSeo } from "./admin/seo-modelo.js";
import { Login } from "./telas/Login.js";
import { Diagnostico } from "./telas/Diagnostico.js";
import { Privacidade, Termos } from "./telas/Termos.js";
import { BlogArtigo, BlogIndice } from "./blog/Blog.js";
import { RolarParaOTopo } from "./componentes/Rolagem.js";
import { Icone } from "./componentes/Marca.js";
import { EH_ISCA, NO_DEFENSIVO, URL_SISTEMA } from "./marca.js";
import { Botao } from "./componentes/ui.js";
import { MetadadosPrivados } from "./componentes/Metadados.js";
import { abrirSuporte, WidgetSuporte } from "./componentes/WidgetSuporte.js";

// Telas autenticadas só são baixadas depois do login. Isso mantém o pacote da
// landing menor sem afetar o HTML estático entregue aos buscadores.
const Documentos = lazy(() => import("./telas/Documentos.js").then(m => ({ default: m.Documentos })));
const Onboarding = lazy(() => import("./telas/Onboarding.js")
  .then((modulo) => ({ default: modulo.Onboarding })));
const TelaRecebimentos = lazy(() => import("./telas/Recebimentos.js").then(m=>({default:m.TelaRecebimentos})));
const Inicio = lazy(() => import("./telas/Inicio.js")
  .then((modulo) => ({ default: modulo.Inicio })));
const TelaConta = lazy(() => import("./telas/Inicio.js")
  .then((modulo) => ({ default: modulo.TelaConta })));
const TelaImoveis = lazy(() => import("./telas/Inicio.js")
  .then((modulo) => ({ default: modulo.TelaImoveis })));
const TelaImovel = lazy(() => import("./telas/Inicio.js")
  .then((modulo) => ({ default: modulo.TelaImovel })));
const TelaMes = lazy(() => import("./telas/Inicio.js")
  .then((modulo) => ({ default: modulo.TelaMes })));
const Escrituracao = lazy(() => import("./telas/Escrituracao.js")
  .then((modulo) => ({ default: modulo.Escrituracao })));
const AdminListaEspera = lazy(() => import("./telas/AdminListaEspera.js")
  .then((modulo) => ({ default: modulo.AdminListaEspera })));
const AdminSuporte = lazy(() => import("./telas/AdminSuporte.js")
  .then((modulo) => ({ default: modulo.AdminSuporte })));
const AdminSeo = lazy(() => import("./admin/AdminSeo.js")
  .then((modulo) => ({ default: modulo.AdminSeo })));

const AdminMetaAds = lazy(() => import("./admin/AdminMetaAds.js")
  .then((modulo) => ({ default: modulo.AdminMetaAds })));

const liberarAcessoAberto = chamar<Record<string, never>, { liberado: true }>(
  "liberarAcessoAberto");
const atualizarPreferenciaMarketing = chamar<
  { atribuicao: NonNullable<ReturnType<typeof capturarAtribuicao>> },
  { salvo: true }
>("atualizarPreferenciaMarketing");

type EstadoAcesso =
  | { tipo: "produto" }
  | { tipo: "erro"; mensagem: string };

/**
 * O BASICO da conta lembrado entre sessoes: so o que a moldura precisa para
 * aparecer na hora (quem e, se o cadastro terminou). Nenhum dado fiscal
 * entra aqui — esses vem sempre frescos do servidor, com esqueleto enquanto
 * chegam (Tiago, 01/09: usuario nunca ve numero desatualizado).
 */
interface ContaLembrada { email: string; cadastroConcluido: boolean }
const CHAVE_CONTA = "ida.conta";
function lerContaLembrada(): ContaLembrada | null {
  try {
    const bruto = localStorage.getItem(CHAVE_CONTA);
    return bruto ? JSON.parse(bruto) as ContaLembrada : null;
  } catch { return null; }
}
function lembrarConta(c: ContaLembrada | null) {
  try {
    if (c) localStorage.setItem(CHAVE_CONTA, JSON.stringify(c));
    else localStorage.removeItem(CHAVE_CONTA);
  } catch { /* sem storage, sem moldura antecipada */ }
}

export function App() {
  // Cada pagina publica mantém seus metadados sincronizados com a rota.
  // Dominio defensivo encaminha para a casa, preservando o caminho.
  useEffect(() => {
    if (NO_DEFENSIVO) {
      window.location.replace(
        `${URL_SISTEMA}${window.location.pathname}${window.location.search}`);
    }
  }, []);
  /*
   * Pos-virada, a isca so tem landing, diagnostico e paginas legais; link de
   * app (lembrete antigo, /mes, /conta...) atravessa para a casa do produto
   * com o caminho intacto.
   */
  useEffect(() => {
    const p = window.location.pathname;
    const daIsca = p === "/" || p.startsWith("/calculadora-imposto-aluguel")
      || p.startsWith("/termos") || p.startsWith("/privacidade")
      || p === "/blog" || p.startsWith("/blog/");
    if (EH_ISCA && !daIsca) {
      window.location.replace(`${URL_SISTEMA}${p}${window.location.search}`);
    }
  }, []);
  const [usuario, setUsuario] = useState<User | null | undefined>(undefined);
  const [adminSeo, setAdminSeo] = useState<boolean | undefined>(undefined);
  const [perfil, setPerfil] = useState<Usuario | null | undefined>(undefined);
  const [acesso, setAcesso] = useState<EstadoAcesso | undefined>(undefined);
  const lembrada = lerContaLembrada();

  useEffect(() => {
    void entrarModoDocumentacao();
  }, []);

  useEffect(() => onIdTokenChanged(auth, (u) => {
    setAdminSeo(u ? undefined : false);
    if (u) void u.getIdTokenResult().then(({ claims }) => {
      if (auth.currentUser === u) setAdminSeo(temPermissaoAdminSeo(claims));
    }).catch(() => { if (auth.currentUser === u) setAdminSeo(false); });
  }), []);

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUsuario(u);
    setAcesso(undefined);
    setPerfil(undefined);
    if (u === null) {
      lembrarConta(null);
      resetarTelemetria();
    }
  }), []);

  useEffect(() => {
    const sincronizar = () => {
      const atribuicao = capturarAtribuicao();
      if (!auth.currentUser || (!atribuicao?.metaMedicao && !atribuicao?.googleMedicao)) return;
      void atualizarPreferenciaMarketing({ atribuicao }).catch(() => undefined);
    };
    window.addEventListener("ida:preferencia-anuncios", sincronizar);
    return () => window.removeEventListener("ida:preferencia-anuncios", sincronizar);
  }, []);

  useEffect(() => {
    if (!usuario) return;
    let encerrado = false;

    void (async () => {
      identificar(usuario.uid);
      try {
        await garantirAcessoProduto(usuario, () => liberarAcessoAberto({}));
        if (!encerrado) setAcesso({ tipo: "produto" });
      } catch {
        if (!encerrado) setAcesso({
          tipo: "erro", mensagem: "Não consegui abrir sua conta. Tente novamente.",
        });
      }
    })();

    return () => { encerrado = true; };
  }, [usuario]);

  useEffect(() => {
    if (!usuario || acesso?.tipo !== "produto") {
      if (usuario === null) setPerfil(null);
      return;
    }
    return onSnapshot(doc(db, "usuarios", usuario.uid), (snap) => {
      const p = snap.exists() ? (snap.data() as Usuario) : null;
      setPerfil(p);
      lembrarConta({ email: usuario.email ?? "",
        cadastroConcluido: Boolean(p?.onboardingConcluidoEm) });
    });
  }, [usuario, acesso?.tipo]);

  // A atividade do produto nao pode depender de emitir ou pagar DARF: ha
  // locadores sem imposto no mes e quem usa apenas gestao, documentos ou
  // cobranca. Uma sessao autenticada ja e um sinal valido de uso.
  useEffect(() => {
    if (usuario && acesso?.tipo === "produto") {
      evento("produto_acessado");
      evento("produto_atividade", { canal: "web", tipo_atividade: "produto_acessado" });
    }
  }, [usuario?.uid, acesso?.tipo]);

  // Fecha a conversao paga no navegador quando o webhook do Asaas atualiza o
  // perfil. O hash de uid+pagoAte vira transaction_id e deduplica recargas e
  // outros aparelhos sem revelar o identificador do usuario ao Google.
  useEffect(() => {
    const pagoAte = perfil?.assinatura?.pagoAte;
    if (usuario && perfil?.assinatura?.status === "ativa" && pagoAte) {
      conversaoGoogle("assinatura_paga_interface", {
        _googleTransacao: `assinatura:${usuario.uid}:${pagoAte}`,
      });
    }
  }, [usuario?.uid, perfil?.assinatura?.status, perfil?.assinatura?.pagoAte]);

  // O blog público não depende da sessão e precisa dos parâmetros da rota
  // já na primeira renderização para preservar o HTML estático.
  if (window.location.pathname === "/blog" || window.location.pathname.startsWith("/blog/")) {
    return <BrowserRouter><Routes>
      <Route path="/blog" element={<BlogIndice />} />
      <Route path="/blog/:slug" element={<BlogArtigo />} />
    </Routes></BrowserRouter>;
  }

  if (usuario && adminSeo === undefined && window.location.pathname.startsWith("/admin/seo")) return <Carregando />;
  if (usuario && window.location.pathname.startsWith("/admin/seo") && (adminSeo || MODO_DOCUMENTACAO)) {
    return <BrowserRouter><MetadadosPrivados /><Suspense fallback={<Carregando />}>
      <AdminSeo />
    </Suspense></BrowserRouter>;
  }

  if (usuario && adminSeo === undefined && window.location.pathname.startsWith("/admin/meta-ads")) return <Carregando />;
  if (usuario && window.location.pathname.startsWith("/admin/meta-ads") && (adminSeo || MODO_DOCUMENTACAO)) {
    return <BrowserRouter><MetadadosPrivados /><Suspense fallback={<Carregando />}>
      <AdminMetaAds />
    </Suspense></BrowserRouter>;
  }

  // Sessao restaurando ou perfil a caminho: com o basico lembrado, a moldura
  // ja aparece (cabecalho + navegacao) e o miolo fica em esqueleto. Sem nada
  // lembrado, a home publica permanece na tela enquanto a sessao e conferida.
  // Isso tambem faz a primeira renderizacao do cliente coincidir com o HTML
  // estatico produzido pelo build, sem apagar e redesenhar a landing.
  if (usuario === undefined || (usuario && acesso === undefined)
      || (usuario && acesso?.tipo === "produto" && perfil === undefined)) {
    if (!lembrada) {
      if (window.location.pathname === "/") {
        return (
          <BrowserRouter>
            <Login />
          </BrowserRouter>
        );
      }
      if (window.location.pathname === "/calculadora-imposto-aluguel") {
        return (
          <BrowserRouter>
            <Diagnostico />
          </BrowserRouter>
        );
      }
      if (window.location.pathname === "/termos") {
        return <BrowserRouter><Termos /></BrowserRouter>;
      }
      if (window.location.pathname === "/privacidade") {
        return <BrowserRouter><Privacidade /></BrowserRouter>;
      }
      return <Carregando />;
    }
    return (
      <BrowserRouter>
        <MetadadosPrivados />
        <Cabecalho email={lembrada.email} comNavegacao={lembrada.cadastroConcluido} />
        <main className="pagina painel" aria-busy="true">
          <div className="esqueleto" style={{ height: 56, width: "40%", marginBottom: 24 }} />
          <div className="esqueleto" style={{ height: 320 }} />
        </main>
      </BrowserRouter>
    );
  }

  // Visitante deslogado tambem tem rotas: o diagnostico e publico de proposito,
  // e exigir login antes de mostrar valor derruba o funil que ele cria.
  if (usuario === null) {
    return (
      <BrowserRouter>
        <RolarParaOTopo />
        <Routes>
          <Route path="/calculadora-imposto-aluguel" element={<Diagnostico />} />
          <Route path="/diagnostico" element={<Navigate to="/calculadora-imposto-aluguel" replace />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/privacidade" element={<Privacidade />} />
          <Route path="/blog" element={<BlogIndice />} />
          <Route path="/blog/:slug" element={<BlogArtigo />} />
          <Route path="*" element={<Login />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (acesso?.tipo === "erro") return <AcessoNegado mensagem={acesso.mensagem} />;
  // Inalcancavel (o bloco acima ja cobriu), mas estreita o tipo para o TS.
  if (perfil === undefined) return <Carregando />;

  const cadastrando = perfil === null || !perfil.onboardingConcluidoEm;

  // O Router envolve os dois fluxos: o cabecalho usa <Link> e quebraria fora dele.
  return (
    <BrowserRouter>
      <RolarParaOTopo />
      <MetadadosPrivados />
      <Cabecalho email={usuario.email ?? ""} comNavegacao={!cadastrando} adminSeo={adminSeo} />
      <Suspense fallback={<Carregando />}>
        {cadastrando ? (
          // Termos e privacidade alcancaveis DURANTE o cadastro: o aceite do
          // final linka os dois, e link morto invalidaria o proprio aceite.
          <Routes>
            <Route path="/termos" element={<Termos />} />
            <Route path="/privacidade" element={<Privacidade />} />
            <Route path="/documentos" element={<Documentos perfil={perfil} />} />
            <Route path="*" element={<Onboarding />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/painel" element={<TelaRecebimentos perfil={perfil} />} />
            <Route path="/impostos" element={<Inicio perfil={perfil} />} />
            <Route path="/impostos/escrituracao/:ano" element={<Escrituracao perfil={perfil} />} />
            <Route path="/" element={<Navigate to="/painel" replace />} />
            <Route path="/mes/:id" element={<TelaMes perfil={perfil} />} />
            <Route path="/imoveis" element={<TelaImoveis />} />
            <Route path="/imoveis/:id" element={<TelaImovel />} />
            <Route path="/conta" element={<TelaConta perfil={perfil} />} />
            <Route path="/documentos" element={<Documentos perfil={perfil} />} />
            <Route path="/termos" element={<Termos />} />
            <Route path="/privacidade" element={<Privacidade />} />
            <Route path="/escrituracao/:ano" element={<Escrituracao perfil={perfil} />} />
            <Route path="/escrituracao" element={<Escrituracao perfil={perfil} />} />
            <Route path="/admin/lista-espera" element={
              usuario.email?.trim().toLowerCase() === "tgloureiro@gmail.com"
                || MODO_DOCUMENTACAO
                ? <AdminListaEspera /> : <Navigate to="/painel" replace />
            } />
            <Route path="/admin/suporte" element={
              usuario.email?.trim().toLowerCase() === "tgloureiro@gmail.com"
                || MODO_DOCUMENTACAO
                ? <AdminSuporte /> : <Navigate to="/painel" replace />
            } />
            <Route path="/admin/seo/*" element={
              adminSeo || MODO_DOCUMENTACAO
                ? <AdminSeo /> : <Navigate to="/painel" replace />
            } />
            {/* Links de lembrete apontam para a competencia = a tela do mes. */}
            <Route path="/competencia/:id" element={<RedirecionaMes />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </Suspense>
      <WidgetSuporte uid={usuario.uid} />
    </BrowserRouter>
  );
}

function RedirecionaMes() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/mes/${id}` : "/painel"} replace />;
}

function Cabecalho({ email, comNavegacao, adminSeo = false }: {
  adminSeo?: boolean;
  email: string;
  comNavegacao?: boolean;
}) {
  const [aberto, setAberto] = useState<"ajuda" | "conta" | null>(null);
  const apoioRef = useRef<HTMLDivElement>(null);
  const [fotoFalhou, setFotoFalhou] = useState(false);
  const nome = auth.currentUser?.displayName?.trim() || email.split("@")[0] || "Minha conta";
  const foto = auth.currentUser?.photoURL;
  useEffect(() => {
    if (!aberto) return;
    const fecharFora = (e: PointerEvent) => {
      if (!apoioRef.current?.contains(e.target as Node)) setAberto(null);
    };
    const fecharTeclado = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        apoioRef.current?.querySelector<HTMLButtonElement>('[aria-expanded="true"]')?.focus();
        setAberto(null);
      }
    };
    document.addEventListener("pointerdown", fecharFora);
    document.addEventListener("keydown", fecharTeclado);
    return () => { document.removeEventListener("pointerdown", fecharFora); document.removeEventListener("keydown", fecharTeclado); };
  }, [aberto]);
  return (
    <header className={`cabecalho${comNavegacao ? " cabecalho--produto" : ""}`}>
      <div className="cabecalho__interno">
        <Link to="/painel" className="marca">
          <Icone tamanho={24} />
          <img src="/logotipo.svg" width="96" alt="alugando.com"/>
        </Link>
        {comNavegacao && (
          <nav className="cabecalho__nav">
            <NavLink to="/painel" end>Recebimentos</NavLink>
            <NavLink to="/impostos">Impostos</NavLink>
            <NavLink to="/imoveis">Imóveis</NavLink>
            <NavLink to="/documentos">Documentos</NavLink>
          </nav>
        )}
        <div className="apoio apoio--separado" ref={apoioRef}>
          <div className="apoio__ancora">
            <button className="apoio__ajuda" aria-expanded={aberto === "ajuda"} aria-controls="menu-ajuda" onClick={() => setAberto(aberto === "ajuda" ? null : "ajuda")}>Ajuda</button>
            {aberto === "ajuda" && <div className="apoio__menu" id="menu-ajuda">
              <button className="apoio__item" onClick={() => { setAberto(null); abrirSuporte("duvida"); }}>Tirar uma dúvida<small>Fale diretamente com a gente</small></button>
              <button className="apoio__item" onClick={() => { setAberto(null); abrirSuporte("sugestao"); }}>Sugerir funcionalidade<small>Vai direto para quem constrói</small></button>
              <button className="apoio__item" onClick={() => { setAberto(null); abrirSuporte("problema"); }}>Reportar problema<small>Conte o que estava fazendo</small></button>
              <button className="apoio__item apoio__item--historico" onClick={() => { setAberto(null); abrirSuporte(); }}>Meus atendimentos<small>Conversas abertas e histórico</small></button>
              {(email.trim().toLowerCase() === "tgloureiro@gmail.com" || MODO_DOCUMENTACAO) && <div className="apoio__admin">
                <NavLink className="apoio__item" to="/admin/lista-espera" onClick={() => setAberto(null)}>Waitlist</NavLink>
                <NavLink className="apoio__item" to="/admin/suporte" onClick={() => setAberto(null)}>Suporte</NavLink>
              </div>}
              {(adminSeo || MODO_DOCUMENTACAO) && <NavLink className="apoio__item" to="/admin/seo" onClick={() => setAberto(null)}>SEO</NavLink>}
            </div>}
          </div>
          <div className="apoio__ancora">
            <button className="apoio__perfil" aria-expanded={aberto === "conta"} aria-controls="menu-conta" onClick={() => setAberto(aberto === "conta" ? null : "conta")}>
              {foto && !fotoFalhou ? <img src={foto} alt="" referrerPolicy="no-referrer" onError={() => setFotoFalhou(true)} /> : <span className="apoio__avatar" aria-hidden="true">{nome.charAt(0).toUpperCase()}</span>}
              <span className="apoio__nome">{nome}</span>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m4 6 4 4 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            {aberto === "conta" && <div className="apoio__menu" id="menu-conta">
              <Link className="apoio__item" to="/conta" onClick={() => setAberto(null)}>Minha conta<small>{email}</small></Link>
              <button className="apoio__item" onClick={() => { setAberto(null); void sair(); }}>Sair</button>
            </div>}
          </div>
        </div>
      </div>
    </header>
  );
}

function AcessoNegado({ mensagem }: { mensagem: string }) {
  return (
    <main className="pagina" style={{ paddingTop: "var(--e8)" }}>
      <div className="pilha pilha-4">
        <h1>Não conseguimos abrir sua conta</h1>
        <p className="suave">{mensagem}</p>
        <div style={{ display: "flex", gap: "var(--e3)", flexWrap: "wrap" }}>
          <Botao onClick={() => window.location.reload()}>Tentar novamente</Botao>
          <Botao variante="secundario" onClick={() => void sair()}>Sair</Botao>
        </div>
      </div>
    </main>
  );
}

/**
 * Splash de carregamento: aparece a cada refresh porque a sessao do Firebase
 * Auth e restaurada de forma assincrona (IndexedDB) e so entao o perfil e
 * lido. Inevitavel, mas curto — entao e a marca pulsando no centro da tela,
 * nao um texto solto no canto.
 */
function Carregando() {
  return (
    <div className="splash" role="status" aria-label="Carregando">
      <div className="splash__marca"><Icone tamanho={40} /></div>
    </div>
  );
}
